import { JWT } from 'next-auth/jwt';
import { jwtDecode } from 'jwt-decode';
import { AxiosPromise } from 'axios';

import { UnrestrictedEnpoints } from '@/core/constants/constants';

import { JWTPayload, RefreshTokenPayload } from '@/features/Auth/interfaces/auth';

import { request } from '@/utils/request';

const getTokenExpirySeconds = (token: string): number | null => {
  if (!token) {
    return null;
  }

  try {
    const { exp } = jwtDecode<JWTPayload>(token);

    return exp;
  } catch {
    return null;
  }
};

export function isTokenCloseToExpiry(token: string) {
  const exp = getTokenExpirySeconds(token);

  if (exp == null) {
    return false;
  }

  const CLOSE_TO_EXPIRY_MINUTES = 1;
  const MINUTES_IN_SECONDS = CLOSE_TO_EXPIRY_MINUTES * 60;

  return exp - Math.floor(Date.now() / 1000) < MINUTES_IN_SECONDS;
}

export function isTokenExpired(token: string) {
  const exp = getTokenExpirySeconds(token);

  if (exp == null) {
    return false;
  }

  return exp - Math.floor(Date.now() / 1000) <= 0;
}

// https://github.com/nextauthjs/next-auth/issues/2071
// temporary fix for the issue above
export const tokenRefreshCache: {
  [key: string]: AxiosPromise<RefreshTokenPayload>;
} = {};

export const refreshAccessToken = async (token: JWT) => {
  const { refreshToken } = token;

  try {
    if (!refreshToken) {
      throw new Error('No refresh token');
    }

    const refreshTokenPromise =
      tokenRefreshCache[refreshToken] ??
      request.post<RefreshTokenPayload>(UnrestrictedEnpoints.REFRESH, {
        refresh: refreshToken,
      });

    if (!tokenRefreshCache[refreshToken]) {
      tokenRefreshCache[refreshToken] = refreshTokenPromise;
    }

    const { data } = await refreshTokenPromise;

    return {
      refreshToken: data.refresh,
      accessToken: data.access,
    };
  } catch (error) {
    return {
      ...token,
      error: 'RefreshAccessTokenError',
    };
  } finally {
    if (refreshToken) {
      delete tokenRefreshCache[refreshToken];
    }
  }
};

export const AUTH_COOKIE_NAME = process.env.NEXTAUTH_URL?.startsWith('http://')
  ? 'next-auth.session-token'
  : '__Secure-next-auth.session-token';
